import type { AudienceRule, RoutingTarget } from './audience.js';
import { matchesAudience } from './audience.js';
import type { FieldSet } from './enums.js';
import type { Principal } from './principal.js';
import { isPrivileged } from './principal.js';



export interface BoardSummary {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly fieldSet: FieldSet;
  readonly position: number;
  readonly isActive: boolean;
  readonly isSystem: boolean;
  readonly intakeTarget: RoutingTarget | null;
  readonly assigneeMayBeCreator: boolean;
  readonly requiresProjectAuthorisation: boolean;
  readonly allowManualStatusChange: boolean;
  readonly viewAudience: readonly AudienceRule[];
  readonly raiseAudience: readonly AudienceRule[];
}

export function canCreateOnBoard(principal: Principal, board: BoardSummary): boolean {
  if (!board.isActive) return false;
  return matchesAudience(principal, board.raiseAudience);
}

export function canViewBoard(principal: Principal, board: BoardSummary): boolean {
  if (!board.isActive) return false;
  if (isPrivileged(principal)) return true;
  if (canCreateOnBoard(principal, board)) return true;

  return matchesAudience(principal, board.viewAudience);
}

export function visibleBoards(
  principal: Principal,
  boards: readonly BoardSummary[],
): readonly BoardSummary[] {
  return boards
    .filter((board) => canViewBoard(principal, board))
    .sort((left, right) => left.position - right.position);
}

export function creatableBoards(
  principal: Principal,
  boards: readonly BoardSummary[],
): readonly BoardSummary[] {
  return boards
    .filter((board) => canCreateOnBoard(principal, board))
    .sort((left, right) => left.position - right.position);
}

export function boardBySlug(
  boards: readonly BoardSummary[],
  slug: string,
): BoardSummary | undefined {
  return boards.find((board) => board.slug === slug);
}
